import { Component, OnDestroy, Renderer2, ViewChild, OnInit } from '@angular/core';
import { NavigationEnd, Router, ActivatedRoute } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { LayoutService } from "./service/app.layout.service";
import { AppSidebarComponent } from "./app.sidebar.component";
import { AppTopBarComponent } from './app.topbar.component';
import { AuthService } from '@auth0/auth0-angular';
import { NGXLogger } from 'ngx-logger';
import { UserService } from '../services/user.service';
import {DrawableMapDialogComponent} from "./components/drawable-map-dialog/drawable-map-dialog.component";
import {DialogService} from "primeng/dynamicdialog";
import { MessageService } from 'primeng/api';

@Component({
    selector: 'app-layout',
    templateUrl: './app.layout.component.html'
})
export class AppLayoutComponent implements OnDestroy, OnInit {

    overlayMenuOpenSubscription: Subscription;

    menuOutsideClickListener: any;

    profileMenuOutsideClickListener: any;

    @ViewChild(AppSidebarComponent) appSidebar!: AppSidebarComponent;

    @ViewChild(AppTopBarComponent) appTopbar!: AppTopBarComponent;

    constructor(
        public layoutService: LayoutService,
        public renderer: Renderer2,
        public router: Router,
        public authService: AuthService,
        private logger: NGXLogger,
        private userService: UserService,
        private dialogService: DialogService,
        private activatedRoute: ActivatedRoute,
        private messageService: MessageService
    ) {
        this.logger.info("INIT @ LAYOUT");
        this.authService.user$.subscribe((user) => {
            if (user != null) {
               this.userService.loadSetUserInformation(user).then(() => {
                  // Utente caricato con successo
                  const appMetadata = user["https://latitude.map-hosting.it/app_metadata"];
                  // ...
              }).catch((error) => {
                  // Gestione errore durante il caricamento informazioni utente
                  console.error('[LAYOUT ERROR] Errore durante il caricamento informazioni utente:', error);
                  this.logger.error('Errore durante il caricamento informazioni utente', error);

                  // Se l'utente è bloccato, il logout e redirect sono già gestiti nel servizio
                  if (error && error.message && error.message.includes('bloccato')) {
                      console.error('[LAYOUT ERROR] Utente bloccato - accesso negato');
                      this.logger.error('Utente bloccato - accesso negato', {
                          userEmail: user?.email,
                          userName: user?.name
                      });
                  }
              });
            }
        });
        this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
            if (!this.menuOutsideClickListener) {
                this.menuOutsideClickListener = this.renderer.listen('document', 'click', event => {
                    const isOutsideClicked = !(this.appSidebar.el.nativeElement.isSameNode(event.target) || this.appSidebar.el.nativeElement.contains(event.target)
                        || this.appTopbar.menuButton.nativeElement.isSameNode(event.target) || this.appTopbar.menuButton.nativeElement.contains(event.target));

                    if (isOutsideClicked) {
                        this.hideMenu();
                    }
                });
            }

            if (!this.profileMenuOutsideClickListener) {
                this.profileMenuOutsideClickListener = this.renderer.listen('document', 'click', event => {
                    const isOutsideClicked = !(this.appTopbar.menu.nativeElement.isSameNode(event.target) || this.appTopbar.menu.nativeElement.contains(event.target)
                        || this.appTopbar.topbarMenuButton.nativeElement.isSameNode(event.target) || this.appTopbar.topbarMenuButton.nativeElement.contains(event.target));

                    if (isOutsideClicked) {
                        this.hideProfileMenu();
                    }
                });
            }

            if (this.layoutService.state.staticMenuMobileActive) {
                this.blockBodyScroll();
            }
        });

        this.router.events.pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => {
                this.hideMenu();
                this.hideProfileMenu();
            });
    }

    hideMenu() {
        this.layoutService.state.overlayMenuActive = false;
        this.layoutService.state.staticMenuMobileActive = false;
        this.layoutService.state.menuHoverActive = false;
        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
            this.menuOutsideClickListener = null;
        }
        this.unblockBodyScroll();
    }

    hideProfileMenu() {
        this.layoutService.state.profileSidebarVisible = false;
        if (this.profileMenuOutsideClickListener) {
            this.profileMenuOutsideClickListener();
            this.profileMenuOutsideClickListener = null;
        }
    }

    blockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.add('blocked-scroll');
        }
        else {
            document.body.className += ' blocked-scroll';
        }
    }

    unblockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.remove('blocked-scroll');
        }
        else {
            document.body.className = document.body.className.replace(new RegExp('(^|\\b)' +
                'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
        }
    }

    get containerClass() {
        return {
            'layout-theme-light': this.layoutService.config().colorScheme === 'light',
            'layout-theme-dark': this.layoutService.config().colorScheme === 'dark',
            'layout-overlay': this.layoutService.config().menuMode === 'overlay',
            'layout-static': this.layoutService.config().menuMode === 'static',
            'layout-static-inactive': this.layoutService.state.staticMenuDesktopInactive && this.layoutService.config().menuMode === 'static',
            'layout-overlay-active': this.layoutService.state.overlayMenuActive,
            'layout-mobile-active': this.layoutService.state.staticMenuMobileActive,
            'p-input-filled': this.layoutService.config().inputStyle === 'filled',
            'p-ripple-disabled': !this.layoutService.config().ripple
        }
    }

    ngOnDestroy() {
        if (this.overlayMenuOpenSubscription) {
            this.overlayMenuOpenSubscription.unsubscribe();
        }

        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
        }
    }

    ngOnInit () {
        this.checkAuth0ErrorParams();
    }

    private checkAuth0ErrorParams(): void {
        // Controllo immediato usando window.location per catturare i params anche se non sono ancora nel router
        const urlParams = new URLSearchParams(window.location.search);
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

      /*   console.log('[AUTH CHECK] Checking error params:', { error, errorDescription, fullUrl: window.location.href });
        this.logger.info('Checking Auth0 error params', { error, errorDescription }); */

        if (error === 'unauthorized' && errorDescription && errorDescription.includes('user is blocked')) {
            const errorMessage = 'Accesso negato: il tuo account è stato bloccato. Contatta l\'amministratore per maggiori informazioni.';

            /* console.error('[AUTH ERROR] Utente bloccato da Auth0:', {
                error: error,
                errorDescription: decodeURIComponent(errorDescription),
                state: urlParams.get('state'),
                fullUrl: window.location.href
            });

            this.logger.error('Utente bloccato da Auth0', {
                error: error,
                errorDescription: decodeURIComponent(errorDescription),
                state: urlParams.get('state')
            });
 */
            // Mostra messaggio di errore
            setTimeout(() => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Accesso Negato',
                    detail: errorMessage,
                    life: 10000,
                    closable: true
                });
            }, 500);

            // Pulisci i parametri dalla URL
            this.router.navigate([], {
                queryParams: {},
                replaceUrl: true
            }).then(() => {
                //console.log('[AUTH] URL cleaned after error');
            });
        }

        // Anche subscription per cambiamenti futuri
        this.activatedRoute.queryParams.subscribe(params => {
            const routeError = params['error'];
            const routeErrorDescription = params['error_description'];

            if (routeError === 'unauthorized' && routeErrorDescription && routeErrorDescription.includes('user is blocked')) {
               /*  console.error('[AUTH ERROR] Utente bloccato (from route params):', {
                    error: routeError,
                    errorDescription: decodeURIComponent(routeErrorDescription),
                    state: params['state']
                });

                this.logger.error('Utente bloccato da Auth0 (route params)', {
                    error: routeError,
                    errorDescription: decodeURIComponent(routeErrorDescription),
                    state: params['state']
                }); */

                this.messageService.add({
                    severity: 'error',
                    summary: 'Accesso Negato',
                    detail: 'Accesso negato: il tuo account è stato bloccato. Contatta l\'amministratore per maggiori informazioni.',
                    life: 10000,
                    closable: true
                });

                this.router.navigate([], {
                    relativeTo: this.activatedRoute,
                    queryParams: {},
                    replaceUrl: true
                });
            }
        });
    }

}
